import type { DrawContext } from './draw';
import type { View } from './project';
import { toScreen } from './project';
import { skyView } from './sky';
import type { SkyTile } from './sky';

/*
 * TILES STILL IN FLIGHT, DRAWN AS CLOUDS — the layer that makes streaming invisible.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1429–1446. Their own comment:
 * *"tiles still in flight read as dim clouds, sized by the name count the index already knows"*.
 *
 * THIS IS WHAT MAKES A STREAMED FIELD FEEL WHOLE. Every renderer that loads in pieces has the same
 * problem: the reader pans, and where names should be there is nothing, and nothing reads as *there
 * is nothing there* rather than as *this has not arrived*. Their answer costs one gradient per tile:
 * the index knows each tile's population before the tile is fetched, so an unloaded region can be
 * drawn as a haze of about the right brightness. When the tile lands, the haze is replaced by the
 * names that were always going to be there, and the reader sees a resolve rather than a pop-in.
 *
 * It is also why the reader is never shown a spinner or a progress bar. The map answers "is
 * something there?" continuously and "what exactly?" as it arrives.
 *
 * ALL OF THE WORLD IS AN ARGUMENT: the index, the tile size, and the per-name brightness — that last
 * one because it converts a POPULATION into an opacity, and how many names a tile holds is the most
 * data-shaped number there is. Theirs averages about 119 names to a tile. The alpha floor and cap
 * are the look and stay.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/** What the layer needs to know about the world. No defaults: all three are ours to supply. */
export interface CloudWorld {
  /** Every tile, with the count each holds. The whole trick rests on knowing that before loading. */
  index: readonly SkyTile[];
  /** How wide a tile is, in world units. */
  tile: number;
  /**
   * How much brightness one name in an unloaded tile is worth.
   *
   * Theirs is 0.00014, which over their roughly 119-names-a-tile puts a typical cloud near the
   * floor and only their densest tiles anywhere near the cap. It is calibrated to THEIR populations,
   * so it is data — a build with a thousand names to a tile would draw every cloud at full strength
   * and lose the very distinction the layer exists to draw.
   */
  alphaPerName: number;
  /** The world's vertical scale. */
  squash: number;
}

/**
 * How far past the screen edge a cloud is still drawn.
 *
 * Wider than the screen because a cloud is a soft gradient: cutting one at the edge would show as a
 * hard line, and the same 1.04 is what `drawSky` pads by.
 */
export const CLOUD_PAD = 1.04;

/** A cloud is this fraction of its tile across — smaller than the tile, so clouds do not tile. */
const CLOUD_SPAN = 0.6;

/** Narrower than this on screen and a cloud is a speck; wider than this and it is a fog bank. */
const CLOUD_MIN_PX = 1.4;
const CLOUD_MAX_SCREEN = 0.9;

/**
 * HOW MANY CLOUDS ONE FRAME MAY DRAW.
 *
 * A budget, like every other number in the fast half of this engine. Pulled right back the viewport
 * can hold most of the index, and the nearest dozen carry the whole impression — the ones beyond
 * that are behind the sky anyway.
 */
const CLOUD_BUDGET = 12;

/** The floor and ceiling on a cloud's brightness. Look, not data. */
const ALPHA_FLOOR = 0.02;
const ALPHA_CAP = 0.16;

/** One cloud to draw: where its tile's middle is, and how many names are waiting in it. */
export interface Cloud {
  x: number;
  y: number;
  n: number;
}

/**
 * Which tiles are worth a cloud: not yet loaded, on screen, and among the nearest few.
 *
 * Distance is measured with the camera's y UNSQUASHED, so "nearest" means nearest in the world —
 * the same measure `tilesWanted` uses to decide what to fetch next, which is what keeps the clouds
 * and the fetch queue agreeing about what matters.
 */
export function cloudsWanted(
  world: CloudWorld,
  view: View,
  ready: (id: string) => boolean,
  budget = CLOUD_BUDGET,
): Cloud[] {
  const z = view.cam.z;
  const span = world.tile * CLOUD_SPAN * z;
  /* Too small to see, or so large that the "cloud" would be a wash over the whole screen. */
  if (span < CLOUD_MIN_PX || span > Math.max(view.view.width, view.view.height) * CLOUD_MAX_SCREEN) return [];
  const v = skyView(view.cam, view.view, world.squash, CLOUD_PAD);
  const sq = world.squash || 1;
  const near: { cloud: Cloud; d: number }[] = [];
  for (const tile of world.index) {
    if (ready(tile.id)) continue;
    if (tile.x1 < v.x0 || tile.x0 > v.x1 || tile.y1 < v.y0 || tile.y0 > v.y1) continue;
    const x = (tile.x0 + tile.x1) / 2;
    const y = (tile.y0 + tile.y1) / 2;
    near.push({ cloud: { x, y, n: tile.n }, d: Math.hypot(x - view.cam.x, y - view.cam.y / sq) });
  }
  near.sort((a, b) => a.d - b.d);
  return near.slice(0, budget).map(entry => entry.cloud);
}

/** How bright one cloud is: its population, floored so an empty tile still shows, and capped. */
export function cloudAlpha(names: number, alphaPerName: number): number {
  return Math.min(ALPHA_CAP, ALPHA_FLOOR + names * alphaPerName);
}

/** How wide a cloud is on screen. The same for all of them — a tile is a tile. */
export function cloudRadius(tile: number, zoom: number): number {
  return tile * CLOUD_SPAN * zoom;
}

/**
 * Draw the haze over everything not yet arrived.
 *
 * The tile index's y coordinates are unsquashed, which is why each is multiplied back by the squash
 * before being projected — `toScreen` divides by it again on the way in.
 */
export function drawClouds(ctx: DrawContext, view: View, world: CloudWorld, clouds: readonly Cloud[]): void {
  const radius = cloudRadius(world.tile, view.cam.z);
  for (const cloud of clouds) {
    const p = toScreen(view, cloud.x, cloud.y * world.squash);
    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
    const a = cloudAlpha(cloud.n, world.alphaPerName).toFixed(3);
    g.addColorStop(0, `rgba(122,138,176,${a})`);
    g.addColorStop(1, 'rgba(122,138,176,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, TAU);
    ctx.fill();
  }
}
